import { Button } from '@/components/ui';
import TooltipWrapper from '@/components/ui/tooltip';
import useCancelOrders from '@/features/trade/order/api/cancel-orders';
import {
  getMigrationBlockTooltipKey,
  useMigrationTradingBlock,
} from '@/hooks/use-migration';
import { useWeekendTrading } from '@/hooks/use-weekend-trading';
import { useAccountStore } from '@/store/use-account-store';
import { NewOrder, useOrdersStore } from '@/store/use-orders-store';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

interface Props extends Pick<NewOrder, 'id'> {}

export default function CancelOrder({ id }: Props) {
  const { t } = useTranslation();
  const { mutate: cancelOrders, isPending: isCancelling } = useCancelOrders();
  const areAllCancelling =
    useIsMutating({ mutationKey: ['/orders/cancel/all'] }) > 0;

  const address = useAccountStore((state) => state.address);
  const symbol = useOrdersStore(
    (state) => state.getOrders(address).orders[id]?.symbol ?? '',
  );
  const { isBlocked: isWeekendBlocked, reopenLabel } =
    useWeekendTrading(symbol);
  const { isBlocked: isMigrationBlocked, reason: migrationReason } =
    useMigrationTradingBlock();
  const isBlocked = isWeekendBlocked || isMigrationBlocked;

  const button = (
    <Button
      variant="outline"
      size="lg"
      disabled={isCancelling || areAllCancelling || isBlocked}
      onClick={() => {
        if (isBlocked) return;
        cancelOrders({ id: [id], showToast: true });
      }}
    >
      {t('openOrdersTable.cancel')}
    </Button>
  );

  if (isMigrationBlocked) {
    return (
      <TooltipWrapper
        content={t(getMigrationBlockTooltipKey(migrationReason))}
        direction="bottom"
        portal
      >
        {button}
      </TooltipWrapper>
    );
  }

  if (isWeekendBlocked) {
    return (
      <TooltipWrapper
        content={t('marketInfo.marketOpensInTooltip', {
          reopen: reopenLabel,
        })}
        direction="bottom"
        portal
      >
        {button}
      </TooltipWrapper>
    );
  }

  return button;
}
